import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Btn, Card, Field, Input, Select, Textarea, PageTitle, ErrorSummary, Notice, useFieldErrors } from "../components/kit";
import { BaselineEditor } from "../components/Baseline";
import { DraftBanner } from "../components/common";
import { useAccess, AccessRequired } from "../components/Access";
import { api, parseError } from "../lib/api";
import { useDraft, useOnline } from "../lib/draft";

const INIT = { name: "", working_identifier: "", species: "dog", breed: "", breed_unknown: false, birth_date: "", age_range: "", sex: "unknown",
  owner_name: "", owner_email: "", owner_phone: "", caregivers: "", referral_source: "", baseline: {} };

export default function DogNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const online = useOnline();
  const acc = useAccess();
  const d = useDraft("new-dog", INIT);
  const [sp] = useSearchParams();
  const training = sp.get("training") === "1";
  const f = d.state;
  const [apiErr, setApiErr] = useState(null);
  const [dups, setDups] = useState(null);
  const [dupReason, setDupReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { list, errFor } = useFieldErrors(apiErr);
  const set = (k) => (e) => d.setState({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const dirty = JSON.stringify(f) !== JSON.stringify(INIT);

  const submit = async (e, reason) => {
    e?.preventDefault();
    if (!online) return d.setPending(true);
    setBusy(true);
    setApiErr(null);
    try {
      const body = { ...f, breed: f.breed_unknown ? "" : f.breed, client_id: d.clientId, duplicate_reason: reason || undefined, is_training: training };
      delete body.breed_unknown;
      const { data } = await api.post("/dogs", body);
      d.clear();
      navigate(`/dogs/${data.id}`);
    } catch (er) {
      const p = parseError(er);
      if (p.network) d.setPending(true);
      else if (p.code === "possible_duplicate") setDups(p.matches);
      else setApiErr(p);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageTitle title={t("dog.new_title")} sub={t("dog.new_sub")} />
      {acc && !acc.practice && <div className="mb-4"><AccessRequired area="practice" testid="dognew-access-required" /></div>}
      {training && <div className="mb-4"><Notice testid="training-case-notice"><b>{t("l.training")}</b> — {t("l.training_hint")}</Notice></div>}
      <form onSubmit={submit} className="space-y-5" noValidate data-testid="new-dog-form">
        <DraftBanner pending={d.pending} restored={d.restored} dirty={dirty} />
        <ErrorSummary errors={list} />
        {dups && (
          <Card className="border-amber-line bg-amber-soft" data-testid="duplicate-warning">
            <h2 className="text-lg font-bold">{t("dog.dup_title")}</h2>
            <ul className="my-3 space-y-2">
              {dups.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
                  <b>{m.name}</b> <span className="text-muted-foreground">{m.number}</span>
                  <span>{t("dog.dup_matching")}: {m.matching.map((x) => t(`field.${x}`)).join(", ")}</span>
                  <Link to={`/dogs/${m.id}`} className="ml-auto"><Btn variant="outline" type="button" testid="duplicate-open-existing-btn">{t("dog.open_existing")}</Btn></Link>
                </li>
              ))}
            </ul>
            <Field label={t("dog.dup_reason")} htmlFor="dup-reason" required><Textarea id="dup-reason" data-testid="duplicate-reason-input" value={dupReason} onChange={(e) => setDupReason(e.target.value)} /></Field>
            <Btn type="button" className="mt-3" variant="soft" disabled={!dupReason.trim() || busy} testid="duplicate-continue-btn" onClick={() => submit(null, dupReason)}>{t("dog.continue_reason")}</Btn>
          </Card>
        )}
        <Card className="space-y-4">
          <h2 className="text-lg font-bold">{t("group.identification")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("field.name")} htmlFor="name" error={errFor("name")} hint={t("dog.name_hint")}><Input id="name" data-testid="dog-name-input" value={f.name} onChange={set("name")} /></Field>
            <Field label={t("field.working_identifier")} htmlFor="wid"><Input id="wid" data-testid="dog-working-id-input" value={f.working_identifier} onChange={set("working_identifier")} /></Field>
            <Field label={t("field.species")} htmlFor="species" required error={errFor("species")}>
              <Select id="species" data-testid="dog-species-select" value={f.species} onChange={set("species")}><option value="dog">{t("species.dog")}</option><option value="other">{t("species.other")}</option></Select>
            </Field>
            <Field label={t("field.breed")} htmlFor="breed">
              <Input id="breed" data-testid="dog-breed-input" disabled={f.breed_unknown} value={f.breed_unknown ? "" : f.breed} placeholder={f.breed_unknown ? t("common.unknown") : ""} onChange={set("breed")} />
              <label className="mt-1 flex min-h-[44px] items-center gap-2 text-sm"><input type="checkbox" data-testid="dog-breed-unknown-checkbox" className="h-5 w-5" checked={f.breed_unknown} onChange={set("breed_unknown")} />{t("common.unknown")}</label>
            </Field>
            <Field label={t("field.birth_date")} htmlFor="bd"><Input id="bd" type="date" data-testid="dog-birth-date-input" value={f.birth_date} onChange={set("birth_date")} /></Field>
            <Field label={t("field.age_range")} htmlFor="ar"><Input id="ar" data-testid="dog-age-range-input" value={f.age_range} onChange={set("age_range")} /></Field>
            <Field label={t("field.sex")} htmlFor="sex">
              <Select id="sex" data-testid="dog-sex-select" value={f.sex} onChange={set("sex")}>{["unknown", "male", "female"].map((s) => <option key={s} value={s}>{t(`sex.${s}`)}</option>)}</Select>
            </Field>
            <Field label={t("field.referral_source")} htmlFor="ref" hint={t("dog.referral_hint")}><Input id="ref" data-testid="dog-referral-input" value={f.referral_source} onChange={set("referral_source")} /></Field>
          </div>
          <h3 className="pt-2 font-bold">{t("dog.owner_contacts")}</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("field.owner_name")} htmlFor="on"><Input id="on" data-testid="dog-owner-name-input" value={f.owner_name} onChange={set("owner_name")} /></Field>
            <Field label={t("field.owner_email")} htmlFor="oe"><Input id="oe" type="email" data-testid="dog-owner-email-input" value={f.owner_email} onChange={set("owner_email")} /></Field>
            <Field label={t("field.owner_phone")} htmlFor="op"><Input id="op" data-testid="dog-owner-phone-input" value={f.owner_phone} onChange={set("owner_phone")} /></Field>
          </div>
          <Field label={t("field.caregivers")} htmlFor="cg"><Input id="cg" data-testid="dog-caregivers-input" value={f.caregivers} onChange={set("caregivers")} /></Field>
        </Card>
        {["coat", "history", "circumstances"].map((g) => (
          <Card key={g} className="space-y-3">
            <h2 className="text-lg font-bold">{t(`group.${g}`)}</h2>
            <BaselineEditor group={g} value={f.baseline[g]} onChange={(v) => d.setState({ ...f, baseline: { ...f.baseline, [g]: v } })} />
          </Card>
        ))}
        <Notice>{t("dog.more_after_save")}</Notice>
        <div className="flex flex-wrap gap-2">
          <Btn type="submit" disabled={busy} testid="dog-save-btn">{t("dog.save")}</Btn>
          <Btn type="button" variant="ghost" testid="dog-discard-btn" onClick={() => d.reset(INIT)}>{t("common.discard_draft")}</Btn>
        </div>
      </form>
    </div>
  );
}
